<template>
  <v-layout>
    <v-flex
      v-if="
        items.length > 0 || !forItinerary || (!noFilters && items.length == 0)
      "
      xs12
    >
      <v-row>
        <v-col cols="12" class="py-0">
          <v-container fluid py-0>
            <v-row align="center">
              <div>
                <h1 class="headline" v-if="!forItinerary">Sales Invoices</h1>
              </div>
              <div>
                <slot name="leftHeader"></slot>
              </div>

              <v-col class="py-0">
                <list-filters
                  :filters="filters"
                  label="Find Invoice"
                  :ref-name="refName"
                  @searchParamsChanged="searchParamsChanged($event)"
                  :expansionPanel="false"
                  :cached-filters="cachedFilters"
                ></list-filters>
              </v-col>
              <div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn class="mr-3" v-on="on" icon @click="onReloadList">
                      <v-icon dark>mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                  <span>Refresh List</span>
                </v-tooltip>
              </div>
              <div>
                <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
              </div>
              <div>
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                ></list-visible-columns-and-filter>
              </div>
              <div>
                <slot></slot>
              </div>
            </v-row>
          </v-container>
        </v-col>
      </v-row>

      <div v-if="showAgGrid" key="ag-grid">
        <ag-grid-vue
          class="ag-theme-alpine"
          id="ag-si-list"
          :columnDefs="agGridColumns"
          :rowData="items"
          rowSelection="multiple"
          :context="agContext"
          @grid-ready="onAgGridReady"
          @columnMoved="onAgColumnMoved"
          @columnResized="onAgColumnResized"
          @sortChanged="onAgSortChanged"
          @selection-changed="onAgSelectionChanged"
          @newColumnsLoaded="onAgNewColumns"
          suppressDragLeaveHidesColumns
        >
        </ag-grid-vue>
        <list-actions
          v-if="selected.length > 0"
          style="top: -30px; position: relative"
          :selected="selected"
          list="salesInvoices"
          :columns="visibleColumns"
          :filter="searchParams"
          :hideExport="hideExport"
          @close="selected = []"
        >
        </list-actions>
      </div>
      <v-data-table
        v-else
        :headers="filteredHeaders"
        :items="items"
        :loading="$apollo.loading"
        v-bind="pagingOptions"
        :search="search"
        item-key="id"
        class="elevation-1"
        show-select
        v-model="selected"
        :fixed-header="fixedHeader"
        hide-default-footer
        @update:options="onListOptionsUpdate"
        :height="gridHeight"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        @current-items="dataTableReady"
      >
        <template v-slot:item.menuActions="{ item }">
          <list-menu-actions
            :item="item"
            @delete="deleteItem"
            @edit="editItem"
            :title-labeler="(item) => item.number"
          ></list-menu-actions>
        </template>
        <template v-slot:item.clientName="{ item }">
          <client-label
            v-model="item.clientName"
            :client-id="item.client ? item.client.id : ''"
          ></client-label>
        </template>
        <template v-slot:item.itineraryReference="{ item }">
          <reference-label
            v-if="item.itinerary"
            v-model="item.itineraryReference"
            :itinerary-id="item.itinerary.id"
          ></reference-label>
        </template>
        <template v-slot:item.agentName="{ item }">
          <agent-label
            v-model="item.agentName"
            :agent-id="item.agent ? item.agent.id : ''"
          ></agent-label>
        </template>
        <template v-slot:item.amount="{ item }">
          {{ item.amount | priceLabeler }}
        </template>
        <template v-slot:item.paid="{ item }">
          {{ item.paid | priceLabeler }}
        </template>
        <template v-slot:item.balance="{ item }">
          {{ item.balance | priceLabeler }}
        </template>
        <template v-slot:item.actions="{ item }">
          <default-list-actions
            :item="item"
            :hideView="true"
            :hide-remove="
              hideRemove || (item.number && item.number.indexOf('CN') > -1)
            "
            @deleteItem="deleteItem"
            @viewItem="viewItem"
            @editItem="editItem"
          ></default-list-actions>
        </template>
        <template v-slot:item.itineraryStartDate="{ item }">{{
          item.itineraryStartDate | formatDate
        }}</template>
        <template v-slot:item.itinerary.balance="{ item }">{{
          item.itinerary.balance | priceLabeler
        }}</template>

        <template v-slot:item.approved="{ item }">
          <v-checkbox
            v-model="item.approved"
            primary
            hide-details
            :disabled="true"
            class="ma-0"
          ></v-checkbox>
        </template>
        <template v-slot:item.exported="{ item }">
          <v-checkbox
            v-model="item.exported"
            primary
            hide-details
            :disabled="true"
            class="ma-0"
          ></v-checkbox>
        </template>
        <template v-slot:item.exportedAt="{ item }">{{
          item.exportedAt | formatDate
        }}</template>
        <template v-slot:item.itineraryStartDate="{ item }">{{
          item.itineraryStartDate | formatDate
        }}</template>
        <template v-slot:item.createdAt="{ item }">{{
          item.createdAt | formatDate
        }}</template>
        <template v-slot:item.approvedAt="{ item }">{{
          item.approvedAt | formatDate
        }}</template>
        <template v-slot:footer>
          <list-actions
            v-if="selected.length > 0"
            :selected="selected"
            list="salesInvoices"
            :columns="visibleColumns"
            :filter="listExportFilters"
            :hideExport="hideExport"
            show-sage
            @update="onInvoiceUpdate"
            @close="selected = []"
          >
            <v-btn fab dark small color="orange" @click="onBatchApprove">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">mdi-check</v-icon>
                </template>
                Batch Approve
              </v-tooltip>
            </v-btn>
            <v-btn
              v-if="showXero"
              fab
              dark
              small
              color="blue darken-1"
              @click="onBatchExportToAccounts"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">mdi-cloud-upload</v-icon>
                </template>
                Export to Accounts
              </v-tooltip>
            </v-btn>
          </list-actions>
          <v-row justify="center" v-if="selected.length > 0">
            <h3 class="py-2 caption">
              {{ selected.length }} record(s) selected
            </h3>
          </v-row>
        </template>
      </v-data-table>

      <get-more-results
        :first="first"
        @limitChange="onLimitChange"
        :items="salesInvoices"
        :totalItems="totalItems"
        :loading="$apollo.loading"
        :loadMoreEnabled="loadMoreEnabled"
        @loadMore="loadMore('salesInvoices')"
      ></get-more-results>
    </v-flex>
    <v-flex v-else-if="forItinerary && items.length == 0 && noFilters" xs12>
      <v-row justify="center" align="center" class="pt-3">
        <v-btn
          fab
          dark
          large
          color="blue darken-1"
          :loading="loading"
          v-if="!loading"
          @click="$emit('generate')"
        >
          <v-icon dark>mdi-receipt</v-icon>
        </v-btn>
      </v-row>
      <v-row justify="center" class="pt-1">
        <v-btn
          text
          color="primary"
          @click="$emit('generate')"
          :disabled="loading"
          :loading="loading"
          >Create New Invoice</v-btn
        >
      </v-row>
    </v-flex>

    <basic-dialog
      :show="showDetailsDialog"
      @close="showDetailsDialog = false"
      max-width="85%"
    >
      <template v-if="selectedItem" v-slot:title>{{
        selectedItem.number
      }}</template>
      <sales-invoice-details
        v-model="selectedItem"
        ref="details"
        @updated="onInvoiceUpdate"
        @csv-data-ready="detailsCsvData = $event"
      ></sales-invoice-details>
      <v-row v-if="debug">
        <code>{{ selectedItem }}</code>
      </v-row>
      <template v-slot:actions>
        <v-btn
          text
          v-if="showXero"
          @click="onExportToXero"
          :disabled="
            (selectedItem && !selectedItem.approved) || exportInProgress
          "
          >Export to XERO</v-btn
        >
        <v-btn
          text
          v-if="showQuickBooks"
          @click="onExportToQuickBooks"
          :disabled="
            (selectedItem && !selectedItem.approved) || exportInProgress
          "
          >Export to QuickBooks</v-btn
        >
        <download-csv
          v-if="showDetailsDialog"
          :data="detailsCsvData"
          delimiter=","
        >
          <v-btn text>
            <v-icon left small>mdi-file-delimited</v-icon> Get CSV
          </v-btn>
        </download-csv>
        <v-btn @click="onSaveAndContinue">Save and Continue</v-btn>
        <v-btn color="primary" @click="onSave">Save</v-btn>
      </template>
    </basic-dialog>
    <task-status
      :show="showBatchApproveTaskStatus"
      v-model="batchApproveTask"
      @complete="onBatchApproveComplete"
    ></task-status>
    <task-status
      :show="showBatchExportTaskStatus"
      v-model="batchExportTask"
      @complete="onBatchExportToAccountsComplete"
    ></task-status>
  </v-layout>
</template>
<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import EventBus from "../../../../store/event-bus";
import {
  ALL_SALES_INVOICES_SEARCH_QUERY,
  GENERATE_SALES_INVOICES,
  SALES_INVOICE_DELETE_MUTATION,
  EXPORT_INVOICE_TO_XERO_MUTATION,
  EXPORT_INVOICE_TO_QUICK_BOOKS_MUTATION,
} from "../../../../constants/graphql";
import GetMoreResults from "../../shared/GetMoreResults";
import DefaultListActions from "../../shared/DefaultListActions";
import ListFilters from "../../shared/list-filters/ListFilters";
import salesInvoicesFilters from "./sales-invoices-filters";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import {
  sortByDate,
  sortByDateWithFormat,
  sortByReference,
  sortByInvNo,
  removeGQLTypename,
} from "../../../../helpers/index";
import ClientLabel from "../../shared/labels/ClientLabel";
import ReferenceLabel from "../../shared/labels/ReferenceLabel";
import AgentLabel from "../../shared/labels/AgentLabel";
import ListMenuActions from "../../shared/ListMenuActions";
import ListActions from "../../shared/list-actions/ListActions";
import AgGridListMixin from "../../../../mixins/AgGridListMixin";
import { AgGridVue } from "ag-grid-vue";
import AgGridSwitch from "../../shared/AgGridSwitch";
import BasicDialog from "../../shared/BasicDialog";
import SalesInvoiceDetails from "./SalesInvoiceDetails";
import BatchApproveMixin from "../purchase-invoices/batch-approve-mixin";
import BatchExportToAccountsMixin from "../purchase-invoices/batch-export-to-accounts-mixin";
import TaskStatus from "../../shared/TaskStatus";
import gql from "graphql-tag";
const CHECK_INVOICE_GENERATION = gql`
  query checkSalesInvoiceGeneration($id: ID) {
    salesInvoiceGenerationInProgress(itineraryId: $id)
  }
`;
export default {
  name: "SalesInovoiceList",

  mixins: [
    ListMixin,
    AgGridListMixin,
    BatchApproveMixin,
    BatchExportToAccountsMixin,
  ],

  components: {
    GetMoreResults,
    DefaultListActions,
    ListFilters,
    ListVisibleColumnsAndFilter,
    ClientLabel,
    ReferenceLabel,
    AgentLabel,
    ListMenuActions,
    ListActions,
    AgGridVue,
    AgGridSwitch,
    BasicDialog,
    SalesInvoiceDetails,
    TaskStatus,
  },

  props: {
    customFilters: {
      type: Object,
      default: () => null,
    },

    loading: {
      type: Boolean,
      default: () => false,
    },

    cachedFilters: {
      type: Boolean,
      default: () => true,
    },
    refName: {
      type: String,
      default: () => "SalesInovoiceList",
    },
    refNameColumns: {
      type: String,
      default: () => "SalesInovoiceListCols",
    },
  },

  data() {
    return {
      debug: false,
      showDetailsDialog: false,
      selectedItem: null,
      salesInvoices: {},
      selected: [],
      salesInvoicesFilters: salesInvoicesFilters,
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "number",
        "clientName",
        "itineraryReference",
        "amount",
        "currencyCode",
        "actions",
      ]),
      searchParams: {},
      headers: [
        {
          text: "",
          sortable: false,
          value: "menuActions",
          name: "menuActions",
        },
        {
          text: "Number",
          value: "number",
          name: "number",
          sortable: true,
          sort: sortByInvNo,
        },
        {
          text: "Client",
          value: "clientName",
          name: "clientName",
          sortable: true,
          cellRendererFramework: "ClientLabel",
        },
        {
          text: "Iti Ref",
          value: "itineraryReference",
          name: "itineraryReference",
          sortable: true,
          sort: sortByReference,
          comparator: sortByReference,
          cellRendererFramework: "ReferenceLabel",
        },
        {
          text: "Iti Start Date",
          value: "itineraryStartDate",
          name: "itineraryStartDate",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          width: "120px",
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.itineraryStartDate),
        },
        {
          text: "Iti Status",
          value: "itineraryStatus",
          sortable: false,
          name: "itineraryStatus",
        },
        {
          text: "Iti Internal Status",
          value: "itineraryInternalStatus",
          sortable: false,
          name: "itineraryInternalStatus",
        },
        {
          text: this.$store.getters.isCPS
            ? "Itinerary Destination"
            : "Iti Brand",
          value: "itineraryBrand",
          sortable: false,
          name: "itineraryBrand",
        },
        {
          text: "Department",
          value: "department",
          sortable: false,
          name: "department",
        },
        {
          text: "Amount",
          value: "amount",
          name: "amount",
          sortable: true,
          valueGetter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.amount),
        },
        {
          text: "Received",
          value: "paid",
          name: "paid",
          sortable: true,
          valueGetter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.paid),
        },

        {
          text: "Iti Balance",
          value: "itinerary.balance",
          name: "balance",
          sortable: true,
          valueGetter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.itinerary.balance),
        },
        {
          text: "Iti Destination",
          value: "itinerary.destinationName",
          name: "destination",
          sortable: false,
          valueGetter: (payload) => payload.data.itinerary.destinationName,
        },

        {
          text: "Currency",
          value: "currencyCode",
          name: "currencyCode",
          sortable: true,
        },
        {
          text: "Agent",
          value: "agentName",
          name: "agentName",
          sortable: true,
          cellRendererFramework: "AgentLabel",
        },
        {
          text: "Agent Account Code",
          value: "agentAccountCode",
          name: "agentAccountCode",
          sortable: false,
        },
        {
          text: "Exported",
          value: "exported",
          name: "exported",
          sortable: true,
        },
        {
          text: "Exported At",
          value: "exportedAt",
          name: "exportedAt",
          sortable: true,
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.exportedAt),
        },
        {
          text: "Exported By",
          value: "exportedByName",
          name: "exportedByName",
          sortable: true,
        },
        {
          text: "Approved By",
          value: "approvedByName",
          sortable: false,
          name: "approvedByName",
        },
        {
          text: "Approved At",
          value: "approvedAt",
          name: "approvedAt",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.approvedAt),
        },
        {
          text: "Invoiced By",
          value: "invoicedByName",
          sortable: false,
          name: "invoicedByName",
        },
        {
          text: "Iti Booked By",
          value: "itinerary.bookedByName",
          name: "itiBookedByName",
          sortable: false,
          valueGetter: (payload) =>
            payload.data.itinerary ? payload.data.itinerary.bookedByName : "",
        },
        {
          text: "Full Invoice",
          value: "approved",
          name: "approved",
          sortable: true,
        },
        {
          text: "Created At",
          value: "createdAt",
          name: "createdAt",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.createdAt),
        },
        {
          text: "External Ref",
          value: "externalReference",
          name: "externalReference",
          sortable: true,
        },
        {
          text: "Iti Internal Ref",
          value: "itineraryInternalRef",
          sortable: false,
          name: "itineraryInternalRef",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
          cellRendererFramework: "DefaultListActions",
          valueGetter: (payload) => ({
            agGridViewOptions: {
              hideRemove: !this.$store.getters.hasAccessTo("finances:delete"),
              hideEdit: false,
            },
          }),
        },
      ],
      expandPanel: "",
      exportInProgress: false,
      detailsCsvData: [],
    };
  },

  computed: {
    listExportFilters() {
      return {
        ...this.customFilters,
        ...this.searchParams,
      };
    },
    hideExport() {
      return !this.$store.getters.hasAccessTo("finances:export");
    },
    showXero() {
      return this.$store.getters.hasAccessTo("finances:xero_export");
    },
    showQuickBooks() {
      return this.$store.getters.hasAccessTo("finances:quick_books");
    },
    hideRemove() {
      return !this.$store.getters.hasAccessTo("finances:delete");
    },
    filters() {
      return salesInvoicesFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        if (this.$store.getters.isCPS) {
          if (f.props.filterName == "brands_eq") {
            f.props.label = "Itinerary Destination";
          }
        }
        return f;
      });
    },
    items() {
      return this.salesInvoices && this.salesInvoices.edges
        ? this.salesInvoices.edges.map(removeGQLTypename)
        : [];
    },
    forItinerary() {
      return this.customFilters && this.customFilters.itinerary_id != "";
    },
    noFilters() {
      return Object.keys(this.searchParams).length < 1;
    },
  },

  methods: {
    onListOptionsUpdate(payload) {
      const listElm =
        document.querySelector(".v-data-table__wrapper") ||
        document.querySelector(".ag-body-viewport");
      if (listElm) {
        listElm.scrollTop = 0;
      }
      const sortByFieldName = payload.sortBy[0];
      //this.first = 50;
      console.log("sort change for: ", this.refName, sortByFieldName);
      if (sortByFieldName) {
        const direction = payload.sortDesc[0] ? "DESC" : "ASC";
        this.sort = { fieldName: sortByFieldName, direction };
      } else {
        this.sort = { fieldName: "id", direction: "DESC" };
      }
      const refNameSort = this.refName
        ? `${this.refName}-sort`
        : `${this.$options.name}-sort`;
      //console.log("updating", refNameColumns);
      this.$store.dispatch("updateLocalSettings", {
        [refNameSort]: { ...this.sort },
      });
    },
    onBatchApprove() {
      this.batchApprove(this.selected, "salesInvoices");
    },
    onBatchExportToAccounts() {
      this.batchExportToAccounts(this.selected, "salesInvoices", "xero");
    },
    onExportToXero() {
      this.exportInProgress = true;
      this.$apollo
        .mutate({
          mutation: EXPORT_INVOICE_TO_XERO_MUTATION,
          variables: {
            input: { invoiceId: this.selectedItem.id },
          },
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: data.exportInvoiceToXero.message,
            color: data.exportInvoiceToXero.status,
          });
          this.exportInProgress = false;
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
          this.exportInProgress = false;
        });
    },
    onExportToQuickBooks() {
      this.exportInProgress = true;
      this.$apollo
        .mutate({
          mutation: EXPORT_INVOICE_TO_QUICK_BOOKS_MUTATION,
          variables: {
            id: this.selectedItem.id,
          },
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: data.exportInvoiceToQuickBooks.message,
            color: data.exportInvoiceToQuickBooks.success ? "success" : "error",
          });
          this.exportInProgress = false;
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
          this.exportInProgress = false;
        });
    },
    onSave() {
      this.$refs.details.saveSalesInvoice();
      this.showDetailsDialog = false;
    },
    onSaveAndContinue() {
      this.$refs.details.saveSalesInvoice();
    },
    onReloadList() {
      if (this.$apollo.queries.salesInvoices != null) {
        this.$apollo.queries.salesInvoices.refetch();
        this.selected = [];
      }
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: SALES_INVOICE_DELETE_MUTATION,
          variables: {
            id: item.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", {
        msg: "Sales invoice removed.",
      });
      if (this.$apollo.queries.salesInvoices != null) {
        this.$apollo.queries.salesInvoices.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
    },

    editItem(item) {
      // this.$router.push("/salesinvoice/" + item.id + "/details");
      this.selectedItem = { ...item };
      this.showDetailsDialog = true;
    },
    onInvoiceUpdate(updatedItem) {
      //this.selected = [];
      if (this.selectedItem.id === updatedItem.id) {
        console.log("updatedItem", updatedItem);
        this.selectedItem.approved = updatedItem.approved;
      }
      if (this.$apollo.queries.salesInvoices != null) {
        this.$apollo.queries.salesInvoices.refetch();
      }
    },
  },

  apollo: {
    salesInvoices: {
      query: ALL_SALES_INVOICES_SEARCH_QUERY,
      variables() {
        return {
          after: this.after,
          first: this.first,
          filter: { ...this.customFilters, ...this.searchParams },
          sort: this.sort,
        };
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        this.totalItems = data.salesInvoices.totalCount;
      },
    },
    salesInvoiceGenerationInProgress: {
      query: CHECK_INVOICE_GENERATION,
      variables() {
        return { id: this.customFilters.itinerary_id };
      },
      fetchPolicy: "network-only",
      skip() {
        return !this.forItinerary;
      },
      result({ data }) {
        if (data.salesInvoiceGenerationInProgress) {
          this.$store.dispatch("showAppSnackbar", {
            msg: "There is a document being created. Please wait a moment and refresh your screen.",
            color: "warning",
          });
        }
      },
    },
  },

  mounted() {
    EventBus.$on("reloadSalesInvoiceList", () => {
      if (this.$apollo.queries.salesInvoices != null) {
        this.$apollo.queries.salesInvoices.refetch();
        this.selected = [];
      }
    });
    //console.log("envs", process.env);
  },
};
</script>
<style scoped>
#ag-si-list {
  height: calc(100vh - 320px);
}
</style>



// WEBPACK FOOTER //
// src/components/gt/finances/sales-invoices/SalesInvoiceList.vue